import React, { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import { getPaymentCalendar } from '../../api/paymentCalendarApi';
import { formatAmountsByCurrency } from '../../utils/currency';
import './BookingCalendar.scss';

function SummaryCard({ label, totals, sublabel }) {
  return (
    <div className="payment-cal__summary-card">
      <span className="payment-cal__summary-label">{label}</span>
      <strong className="payment-cal__summary-value">{formatAmountsByCurrency(totals?.amounts)}</strong>
      <span className="payment-cal__summary-meta">
        {totals?.count || 0} payment{(totals?.count || 0) === 1 ? '' : 's'}
        {sublabel ? ` · ${sublabel}` : ''}
      </span>
    </div>
  );
}

function MonthAmountLine({ stats }) {
  const verifiedCount = stats?.verified?.count || 0;
  const pendingCount = stats?.pending?.count || 0;
  const amountLabel = formatAmountsByCurrency(stats?.amounts);

  return (
    <>
      <span className="booking-cal__month-line-total d-block text-center">{amountLabel}</span>
      {verifiedCount > 0 && (
        <span className="booking-cal__month-line-detail d-block text-center">
          {verifiedCount} verified{pendingCount > 0 ? ` · ${pendingCount} pending` : ''}
        </span>
      )}
      {verifiedCount === 0 && pendingCount > 0 && (
        <span className="booking-cal__month-line-detail d-block text-center">{pendingCount} pending</span>
      )}
    </>
  );
}

export default function CustomerPaymentsCalendar({ selectedMonth, onMonthSelect, onYearChange }) {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await getPaymentCalendar({ year });
      setSummary(res.data);
    } catch (e) {
      console.warn('CustomerPaymentsCalendar error', e);
      setError(true);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    if (onYearChange) onYearChange(year);
  }, [year, onYearChange]);

  const selectedMonthMeta = summary?.months?.find((m) => m.month === selectedMonth);
  const monthTotals = selectedMonthMeta
    ? { count: selectedMonthMeta.verified?.count || 0, amounts: selectedMonthMeta.amounts }
    : null;

  const handleYearChange = (nextYear) => {
    setYear(nextYear);
    onMonthSelect(null);
  };

  return (
    <section className="payment-cal mb-4">
      <div className="d-flex flex-wrap align-items-center justify-content-between mb-3">
        <div>
          <h4 className="mb-1">Payment totals</h4>
          <p className="text-muted mb-0 small">
            Verified payments received · click a month to filter the list below
          </p>
        </div>
        <div className="booking-cal__year-nav">
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => handleYearChange(year - 1)} aria-label="Previous year">‹</button>
          <span className="booking-cal__year-label">{year}</span>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => handleYearChange(year + 1)} aria-label="Next year">›</button>
        </div>
      </div>

      {error && (
        <div className="alert alert-warning py-2">
          Could not load payment totals.{' '}
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={fetchSummary}>Retry</button>
        </div>
      )}

      {!loading && !error && summary && (
        <div className="payment-cal__summary-row mb-3">
          <SummaryCard label={`${year} verified`} totals={summary.year_totals} sublabel="money received" />
          {selectedMonth && monthTotals && (
            <SummaryCard
              label={moment(`${year}-${String(selectedMonth).padStart(2, '0')}-01`).format('MMMM YYYY')}
              totals={monthTotals}
              sublabel="verified this month"
            />
          )}
        </div>
      )}

      {loading && (
        <div className="text-center py-3">
          <div className="spinner-border spinner-border-sm text-primary" role="status"><span className="sr-only">Loading…</span></div>
        </div>
      )}

      {!loading && !error && summary && (
        <div className="booking-cal__grid mb-2" role="list">
          {summary.months.map((m) => {
            const isSelected = selectedMonth === m.month;
            const isCurrentMonth = year === currentYear && m.month === new Date().getMonth() + 1;
            const hasVerified = (m.verified?.count || 0) > 0;

            return (
              <button
                key={m.year_month}
                type="button"
                role="listitem"
                className={`booking-cal__cell${isSelected ? ' booking-cal__cell--selected' : ''}${isCurrentMonth ? ' booking-cal__cell--current' : ''}${!hasVerified && (m.pending?.count || 0) > 0 ? ' booking-cal__cell--pending' : ''}`}
                onClick={() => onMonthSelect(isSelected ? null : m.month)}
                aria-pressed={isSelected}
              >
                <span className="booking-cal__cell-label">{m.label}</span>
                <MonthAmountLine stats={m} />
              </button>
            );
          })}
        </div>
      )}

      {!selectedMonth && !loading && !error && (
        <p className="text-muted text-center small mb-0">Click a month to filter payments for that month.</p>
      )}
    </section>
  );
}
